import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CrmGraphql } from '../../shared/constants/graphql-constants';
import {
  getContractLevelUsersQuery,
  searchContractPortalUsersQuery,
  updateContractPortalUserLinkStatusMutation,
} from '../up-tab/up-tab-data-queries.constant';
import {
  IContractPortalLinkHistoryItem,
  IContractPortalPaginatedResult,
  IContractPortalUser,
  IContractPortalUserLinkResponse,
  IContractPortalUserProfile,
  IContractPortalUsersAssociationStatus,
} from '../up-tab/up-tab.interface';
import {
  getContractPortalUsersAssociationStatusQuery,
  getLinkedContractPortalUsersQuery,
  getPortalUserProfileContractHistoryQuery,
  getUserProfileTabEligibilityQuery,
  searchPortalUserProfilesQuery,
} from './user-profile-tab-data-queries.constant';

@Injectable()
export class UserProfileTabService {
  constructor(private apollo: Apollo) {}

  /** Separate from Smart Agent eligibility. True only for users configured for this tab. */
  checkUserProfileTabEligibility(azureUserId: string) {
    return this.apollo.use(CrmGraphql.QueryNameSpace).query<{ getUserProfileTabEligibility: boolean }>({
      query: getUserProfileTabEligibilityQuery,
      variables: { azureUserId },
      fetchPolicy: 'network-only',
    });
  }

  searchPortalUserProfiles(
    email: string,
    azureUserId?: string,
  ): Observable<IContractPortalPaginatedResult<IContractPortalUserProfile>> {
    return this.apollo
      .use(CrmGraphql.NameSpace)
      .query<{ searchPortalUserProfiles: IContractPortalPaginatedResult<IContractPortalUserProfile> }>({
        query: searchPortalUserProfilesQuery,
        variables: { email, azureUserId },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map(
          (result) =>
            result.data?.searchPortalUserProfiles ?? {
              items: [],
              count: 0,
            },
        ),
      );
  }

  getLinkedContractPortalUsers(
    contractNumber: string,
    azureUserId?: string,
  ): Observable<IContractPortalPaginatedResult<IContractPortalUser>> {
    return this.apollo
      .use(CrmGraphql.NameSpace)
      .query<{ getLinkedContractPortalUsers: IContractPortalPaginatedResult<IContractPortalUser> }>({
        query: getLinkedContractPortalUsersQuery,
        variables: { contractNumber, azureUserId },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map(
          (result) =>
            result.data?.getLinkedContractPortalUsers ?? {
              items: [],
              count: 0,
            },
        ),
      );
  }

  getPortalUserProfileContractHistory(
    params: { userLoginId?: number; email?: string },
    azureUserId?: string,
  ): Observable<IContractPortalPaginatedResult<IContractPortalLinkHistoryItem>> {
    return this.apollo
      .use(CrmGraphql.NameSpace)
      .query<{
        getPortalUserProfileContractHistory: IContractPortalPaginatedResult<IContractPortalLinkHistoryItem>;
      }>({
        query: getPortalUserProfileContractHistoryQuery,
        variables: { ...params, azureUserId },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map(
          (result) =>
            result.data?.getPortalUserProfileContractHistory ?? {
              items: [],
              count: 0,
            },
        ),
      );
  }

  searchContractPortalUsers(
    contractNumber: string,
    email: string,
    azureUserId?: string,
  ): Observable<IContractPortalUser[]> {
    return this.apollo
      .use(CrmGraphql.NameSpace)
      .query<{ searchContractPortalUsers: IContractPortalUser[] }>({
        query: searchContractPortalUsersQuery,
        variables: { contractNumber, email, azureUserId },
        fetchPolicy: 'network-only',
      })
      .pipe(map((result) => result.data?.searchContractPortalUsers ?? []));
  }

  getContractLevelUsers(
    contractNumber: string,
    azureUserId?: string,
  ): Observable<IContractPortalUser[]> {
    return this.apollo
      .use(CrmGraphql.NameSpace)
      .query<{ getContractLevelUsers: IContractPortalUser[] }>({
        query: getContractLevelUsersQuery,
        variables: { contractNumber, azureUserId },
        fetchPolicy: 'network-only',
      })
      .pipe(map((result) => result.data?.getContractLevelUsers ?? []));
  }

  getContractPortalUsersAssociationStatus(
    contractNumber: string,
    azureUserId?: string,
  ): Observable<IContractPortalUsersAssociationStatus> {
    return this.apollo
      .use(CrmGraphql.NameSpace)
      .query<{ getContractPortalUsersAssociationStatus: IContractPortalUsersAssociationStatus }>({
        query: getContractPortalUsersAssociationStatusQuery,
        variables: { contractNumber, azureUserId },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map(
          (result) =>
            result.data?.getContractPortalUsersAssociationStatus ?? {
              associated: false,
              canLink: false,
              message: '',
              linkedUsers: [],
            },
        ),
      );
  }

  updateContractPortalUserLinkStatus(
    contractNumber: string,
    userLoginId: number,
    link: boolean,
    azureUserId?: string,
  ): Observable<IContractPortalUserLinkResponse> {
    return this.apollo
      .use(CrmGraphql.NameSpace)
      .mutate<{ updateContractPortalUserLinkStatus: IContractPortalUserLinkResponse }>({
        mutation: updateContractPortalUserLinkStatusMutation,
        variables: { contractNumber, userLoginId, link, azureUserId },
      })
      .pipe(
        map(
          (result) =>
            result.data?.updateContractPortalUserLinkStatus ?? {
              message: '',
            },
        ),
      );
  }
}
